import * as React from 'react';
import { useState } from 'react';

import { useSetAtom } from 'jotai';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import type { SmallButtonProps } from '~shared/components/buttons';
import { SmallButton } from '~shared/components/buttons';
import { IconAI } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import { useGenerateInsightsMutation } from '~teacher/hook';
import { handleInsightsError } from '~teacher/hook/activity/insights/handleInsightsError';
import { pendingInsightsAtom } from '~teacher/hook/activity/insights/pendingInsightsAtom';
import { clearToastTracking } from '~teacher/hook/activity/insights/useGlobalInsightsPolling';

interface InsightsGenerateInsightsButtonProps {
  isLoading: boolean;
  onClickCapture: SmallButtonProps['onClickCapture'];
}

export function InsightsGenerateInsightsButton({
  isLoading,
  onClickCapture,
}: InsightsGenerateInsightsButtonProps) {
  return (
    <SmallButton
      noWrap
      isLoading={isLoading}
      onClickCapture={onClickCapture}
      xPadding={0}
    >
      <div className="flex items-center pl-2 pr-4">
        <IconAI />
        {translate('Analyze Report')}
      </div>
    </SmallButton>
  );
}

interface UseInsightsGenerateInsightsButtonParams {
  activityInstanceId: number;
  setShowPaywall: (show: boolean) => void;
  averageScoreRate?: number | null;
}

interface UseInsightsGenerateInsightsButtonResult {
  isLoading: boolean;
  onClickCapture: SmallButtonProps['onClickCapture'];
  showNoInsightsDialog: boolean;
  setShowNoInsightsDialog: (show: boolean) => void;
}

export function useInsightsGenerateInsightsButton({
  activityInstanceId,
  setShowPaywall,
  averageScoreRate,
}: UseInsightsGenerateInsightsButtonParams): UseInsightsGenerateInsightsButtonResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showNoInsightsDialog, setShowNoInsightsDialog] = useState(false);
  const postGenerateInsightsMutation = useGenerateInsightsMutation();
  const dispatch = useSetAtom(pendingInsightsAtom);

  const onClickCapture: SmallButtonProps['onClickCapture'] = async (event) => {
    event.stopPropagation();
    
    // Check if avg score is 100%
    if (averageScoreRate === 100) {
      setShowNoInsightsDialog(true);
      return;
    } else {
      setShowNoInsightsDialog(false);
    }
    
    setIsGenerating(true);
    sendTeacherAmplitudeEvent(EventTypes.ClickGenerateAIInsights);

    try {
      await postGenerateInsightsMutation.mutateAsync({
        activityInstanceId,
      });

      // Clear toast tracking so toasts can show again for this activity
      clearToastTracking(activityInstanceId);

      // Marking this activity as having a pending insight so the UI can react later
      // Only add to pending if the mutation succeeded
      dispatch({ type: 'add', activityInstanceId });
    } catch (error) {
      // Handle the error based on its type
      const errorResult = handleInsightsError(error);

      switch (errorResult.type) {
        case 'paywall':
          setShowPaywall(true);
          break;
        case 'toast':
          toast.critical(errorResult.message);
          break;
        case 'silent':
          /* Component will handle showing the message
            Currently runs when InsufficientDataError is thrown
          */
          break;
      }

      // Don't add to pending insights on error - the mutation onError handler
      // will update the query cache to show the appropriate state
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isLoading: isGenerating,
    onClickCapture: onClickCapture,
    showNoInsightsDialog,
    setShowNoInsightsDialog,
  };
}
